<template>
  <div class="space-y-4">
    <!-- Search and Filters -->
    <div v-if="searchable || $slots.filters" class="flex flex-wrap items-center gap-4">
      <div v-if="searchable" class="flex-1 min-w-[200px]">
        <input
          v-model="searchQuery"
          type="search"
          placeholder="Search..."
          aria-label="Search table"
          class="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
        />
      </div>
      <slot name="filters"></slot>
    </div>

    <!-- Table Container -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft overflow-hidden">
      <!-- Empty State -->
      <div v-if="!loading && !sortedData.length" class="py-12 text-center">
        <slot name="empty">
          <div class="flex flex-col items-center gap-3">
            <span class="material-icons text-5xl text-neutral-400 dark:text-neutral-600" aria-hidden="true">inbox</span>
            <p class="text-neutral-600 dark:text-neutral-400">{{ emptyText }}</p>
          </div>
        </slot>
      </div>

      <!-- Table (renders skeleton rows while loading so the layout never shifts) -->
      <div v-else class="overflow-x-auto" :aria-busy="loading ? 'true' : null">
        <table class="w-full">
          <thead class="bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
            <tr>
              <!-- Selection Column -->
              <th v-if="selectable" scope="col" class="px-4 py-3 text-center w-12">
                <input
                  type="checkbox"
                  :checked="isAllSelected"
                  :indeterminate.prop="isSomeSelected"
                  :disabled="loading"
                  aria-label="Select all rows"
                  @change="toggleSelectAll"
                  class="rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 dark:bg-neutral-800 disabled:opacity-50"
                />
              </th>

              <!-- Index Column -->
              <th v-if="showIndex" scope="col" class="px-4 py-3 text-center text-sm font-semibold text-neutral-700 dark:text-neutral-300 w-16">
                #
              </th>

              <!-- Data Columns -->
              <th
                v-for="column in columns"
                :key="column.key"
                scope="col"
                :aria-sort="ariaSort(column)"
                :style="{ width: column.width }"
                :class="[
                  'px-4 py-3 text-sm font-semibold text-neutral-700 dark:text-neutral-300',
                  column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left',
                  column.headerClass
                ]"
              >
                <button
                  v-if="column.sortable !== false"
                  type="button"
                  @click="sortBy(column.key)"
                  class="flex items-center gap-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 w-full cursor-pointer hover:text-primary-600 dark:hover:text-primary-400"
                  :class="column.align === 'center' ? 'justify-center' : column.align === 'right' ? 'justify-end' : ''"
                >
                  <span>{{ column.label }}</span>
                  <span v-if="sortColumn === column.key" class="material-icons text-sm text-primary-600 dark:text-primary-400" aria-hidden="true">
                    {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                  </span>
                  <span v-else class="material-icons text-sm text-neutral-400" aria-hidden="true">unfold_more</span>
                </button>
                <div v-else class="flex items-center gap-2" :class="column.align === 'center' ? 'justify-center' : column.align === 'right' ? 'justify-end' : ''">
                  <span>{{ column.label }}</span>
                </div>
              </th>

              <!-- Actions Column -->
              <th v-if="$slots.actions" scope="col" class="px-4 py-3 text-center text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                Actions
              </th>
            </tr>
          </thead>

          <!-- Skeleton Rows (loading) -->
          <tbody v-if="loading" class="divide-y divide-neutral-200 dark:divide-neutral-700">
            <tr v-for="n in 6" :key="`skeleton-${n}`">
              <td v-if="selectable" class="px-4 py-3">
                <div class="animate-pulse h-4 w-4 mx-auto rounded bg-neutral-200 dark:bg-neutral-700"></div>
              </td>
              <td v-if="showIndex" class="px-4 py-3">
                <div class="animate-pulse h-4 w-6 mx-auto rounded bg-neutral-200 dark:bg-neutral-700"></div>
              </td>
              <td v-for="column in columns" :key="column.key" :style="{ width: column.width }" class="px-4 py-3">
                <div class="animate-pulse h-4 rounded bg-neutral-200 dark:bg-neutral-700"></div>
              </td>
              <td v-if="$slots.actions" class="px-4 py-3">
                <div class="animate-pulse h-4 w-12 mx-auto rounded bg-neutral-200 dark:bg-neutral-700"></div>
              </td>
            </tr>
          </tbody>

          <tbody v-else class="divide-y divide-neutral-200 dark:divide-neutral-700">
            <tr
              v-for="(row, index) in paginatedData"
              :key="row[rowKey] || index"
              :tabindex="isClickable ? 0 : null"
              @click="isClickable && $emit('row-click', row)"
              @keydown.enter="handleRowKeydown($event, row)"
              @keydown.space="handleRowKeydown($event, row)"
              :class="[
                'transition-colors',
                isClickable ? 'cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700 outline-none focus-visible:bg-neutral-50 dark:focus-visible:bg-neutral-800' : '',
                selectedRows.includes(row[rowKey]) ? 'bg-primary-50 dark:bg-primary-900/20' : ''
              ]"
            >
              <!-- Selection Column -->
              <td v-if="selectable" class="px-4 py-3 text-center">
                <input
                  type="checkbox"
                  :checked="selectedRows.includes(row[rowKey])"
                  :aria-label="`Select row ${row[rowKey] ?? index + 1}`"
                  @change="toggleRow(row)"
                  @click.stop
                  class="rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 dark:bg-neutral-800"
                />
              </td>

              <!-- Index Column -->
              <td v-if="showIndex" class="px-4 py-3 text-center text-sm text-neutral-600 dark:text-neutral-400">
                {{ (currentPage - 1) * itemsPerPage + index + 1 }}
              </td>

              <!-- Data Columns -->
              <td
                v-for="column in columns"
                :key="column.key"
                :style="{ width: column.width }"
                :class="[
                  'px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300',
                  column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left',
                  column.cellClass
                ]"
              >
                <slot :name="`cell-${column.key}`" :row="row" :value="row[column.key]">
                  {{ row[column.key] }}
                </slot>
              </td>

              <!-- Actions Column -->
              <td v-if="$slots.actions" class="px-4 py-3 text-center">
                <slot name="actions" :row="row"></slot>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="pagination && !loading && sortedData.length" class="border-t border-neutral-200 dark:border-neutral-700 px-6 py-4">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <!-- Items per page selector -->
          <div class="flex items-center gap-3">
            <label :for="perPageId" class="text-sm text-neutral-600 dark:text-neutral-400">Rows per page:</label>
            <select
              :id="perPageId"
              v-model.number="itemsPerPage"
              class="px-3 py-1.5 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm"
            >
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </div>

          <!-- Pagination info and controls -->
          <div class="flex items-center gap-6">
            <span class="text-sm text-neutral-600 dark:text-neutral-400">
              {{ (currentPage - 1) * itemsPerPage + 1 }} - {{ Math.min(currentPage * itemsPerPage, sortedData.length) }} of {{ sortedData.length }}
            </span>

            <div class="flex items-center gap-2">
              <button
                type="button"
                aria-label="First page"
                @click="currentPage = 1"
                :disabled="currentPage === 1"
                class="inline-flex items-center justify-center min-w-[40px] min-h-[40px] rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed text-neutral-700 dark:text-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <span class="material-icons text-lg" aria-hidden="true">first_page</span>
              </button>
              <button
                type="button"
                aria-label="Previous page"
                @click="currentPage--"
                :disabled="currentPage === 1"
                class="inline-flex items-center justify-center min-w-[40px] min-h-[40px] rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed text-neutral-700 dark:text-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <span class="material-icons text-lg" aria-hidden="true">chevron_left</span>
              </button>

              <span class="text-sm font-medium text-neutral-900 dark:text-neutral-100 min-w-[80px] text-center">
                Page {{ currentPage }} of {{ totalPages }}
              </span>

              <button
                type="button"
                aria-label="Next page"
                @click="currentPage++"
                :disabled="currentPage === totalPages"
                class="inline-flex items-center justify-center min-w-[40px] min-h-[40px] rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed text-neutral-700 dark:text-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <span class="material-icons text-lg" aria-hidden="true">chevron_right</span>
              </button>
              <button
                type="button"
                aria-label="Last page"
                @click="currentPage = totalPages"
                :disabled="currentPage === totalPages"
                class="inline-flex items-center justify-center min-w-[40px] min-h-[40px] rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed text-neutral-700 dark:text-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <span class="material-icons text-lg" aria-hidden="true">last_page</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Always-rendered live region: inserting role=status together with its
         text (v-if) is ignored by most screen readers -->
    <p class="sr-only" role="status" aria-atomic="false">{{ loading ? loadingText : '' }}</p>
  </div>
</template>

<script>
import { ref, computed, watch, getCurrentInstance } from 'vue'

let dataTableUid = 0

export default {
  name: 'DataTable',
  props: {
    // Data array
    data: {
      type: Array,
      default: () => []
    },
    // Column definitions
    columns: {
      type: Array,
      required: true
    },
    // Unique row key
    rowKey: {
      type: String,
      default: 'id'
    },
    // Enable pagination
    pagination: {
      type: Boolean,
      default: true
    },
    // Enable search
    searchable: {
      type: Boolean,
      default: false
    },
    // Enable row selection
    selectable: {
      type: Boolean,
      default: false
    },
    // Show index column
    showIndex: {
      type: Boolean,
      default: false
    },
    // Row interactivity (tabindex, cursor, Enter/Space, row-click emit).
    // null = auto: interactive only when the consumer binds @row-click, so
    // listener-less tables don't grow dead tab stops and pointer cursors.
    clickable: {
      type: Boolean,
      default: null
    },
    // External selection sync (v-model:selected); null = uncontrolled
    selected: {
      type: Array,
      default: null
    },
    // External page sync (v-model:page) so views with filters outside the
    // table can reset pagination; null = uncontrolled
    page: {
      type: Number,
      default: null
    },
    // Loading state
    loading: {
      type: Boolean,
      default: false
    },
    // Loading text
    loadingText: {
      type: String,
      default: 'Loading data...'
    },
    // Empty state text
    emptyText: {
      type: String,
      default: 'No data available'
    },
    // Default sort column
    defaultSortColumn: {
      type: String,
      default: null
    },
    // Default sort direction
    defaultSortDirection: {
      type: String,
      default: 'asc',
      validator: (value) => ['asc', 'desc'].includes(value)
    }
  },
  emits: ['row-click', 'selection-change', 'sort', 'page-change', 'update:selected', 'update:page'],
  setup(props, { emit }) {
    // row-click is a declared emit so it never reaches attrs — read the
    // listener's presence off the vnode for the clickable=null auto mode.
    const vm = getCurrentInstance()
    const isClickable = computed(() =>
      props.clickable !== null ? props.clickable : !!(vm && vm.vnode.props && vm.vnode.props.onRowClick)
    )
    const searchQuery = ref('')
    const currentPage = ref(1)
    const itemsPerPage = ref(25)
    const sortColumn = ref(props.defaultSortColumn)
    const sortDirection = ref(props.defaultSortDirection)
    const selectedRows = ref([])
    const perPageId = `datatable-rows-per-page-${++dataTableUid}`

    // Filter data based on search
    const filteredData = computed(() => {
      if (!props.searchable || !searchQuery.value) {
        return props.data
      }

      const query = searchQuery.value.toLowerCase()
      return props.data.filter(row => {
        return props.columns.some(column => {
          const value = row[column.key]
          return value && String(value).toLowerCase().includes(query)
        })
      })
    })

    // Sort data
    const sortedData = computed(() => {
      if (!sortColumn.value) {
        return filteredData.value
      }

      return [...filteredData.value].sort((a, b) => {
        const aVal = a[sortColumn.value]
        const bVal = b[sortColumn.value]

        if (aVal === bVal) return 0
        if (aVal == null) return 1
        if (bVal == null) return -1

        const comparison = aVal < bVal ? -1 : 1
        return sortDirection.value === 'asc' ? comparison : -comparison
      })
    })

    // Paginate data
    const paginatedData = computed(() => {
      if (!props.pagination) {
        return sortedData.value
      }

      const start = (currentPage.value - 1) * itemsPerPage.value
      const end = start + itemsPerPage.value
      return sortedData.value.slice(start, end)
    })

    // Total pages
    const totalPages = computed(() => {
      if (!props.pagination) return 1
      return Math.ceil(sortedData.value.length / itemsPerPage.value) || 1
    })

    // Check if all rows are selected
    const isAllSelected = computed(() => {
      return paginatedData.value.length > 0 && paginatedData.value.every(row => selectedRows.value.includes(row[props.rowKey]))
    })

    // Some (but not all) rows on the current page are selected
    const isSomeSelected = computed(() => {
      if (!paginatedData.value.length || isAllSelected.value) return false
      return paginatedData.value.some(row => selectedRows.value.includes(row[props.rowKey]))
    })

    // aria-sort value for a column header
    const ariaSort = (column) => {
      if (column.sortable === false) return null
      if (sortColumn.value !== column.key) return 'none'
      return sortDirection.value === 'asc' ? 'ascending' : 'descending'
    }

    // Sort by column
    const sortBy = (column) => {
      if (sortColumn.value === column) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
      } else {
        sortColumn.value = column
        sortDirection.value = 'asc'
      }
      emit('sort', { column, direction: sortDirection.value })
    }

    const emitSelection = () => {
      emit('selection-change', selectedRows.value)
      emit('update:selected', selectedRows.value)
    }

    // Toggle row selection
    const toggleRow = (row) => {
      const key = row[props.rowKey]
      const index = selectedRows.value.indexOf(key)
      if (index > -1) {
        selectedRows.value.splice(index, 1)
      } else {
        selectedRows.value.push(key)
      }
      emitSelection()
    }

    // Toggle select all
    const toggleSelectAll = () => {
      if (isAllSelected.value) {
        paginatedData.value.forEach(row => {
          const index = selectedRows.value.indexOf(row[props.rowKey])
          if (index > -1) {
            selectedRows.value.splice(index, 1)
          }
        })
      } else {
        paginatedData.value.forEach(row => {
          const key = row[props.rowKey]
          if (!selectedRows.value.includes(key)) {
            selectedRows.value.push(key)
          }
        })
      }
      emitSelection()
    }

    // Keyboard activation for clickable rows; ignore keydowns bubbling from
    // inner controls (checkboxes, action buttons) so their defaults survive
    const handleRowKeydown = (event, row) => {
      if (!isClickable.value) return
      if (event.target !== event.currentTarget) return
      event.preventDefault()
      emit('row-click', row)
    }

    // Mirror external selection (v-model:selected) into internal state
    watch(() => props.selected, (val) => {
      if (val == null) return
      if (val === selectedRows.value) return
      selectedRows.value = [...val]
    }, { immediate: true })

    // Mirror external page (v-model:page) into internal state
    watch(() => props.page, (val) => {
      if (val == null) return
      if (val !== currentPage.value) currentPage.value = val
    }, { immediate: true })

    // Clamp page when the page count shrinks (data mutations, filtering)
    watch(totalPages, (total) => {
      if (currentPage.value > total) {
        currentPage.value = Math.max(1, total)
      }
    })

    // Reset page when search changes
    watch(searchQuery, () => {
      currentPage.value = 1
    })

    // Reset page when page size changes
    watch(itemsPerPage, () => {
      currentPage.value = 1
    })

    // Emit page change
    watch(currentPage, (newPage) => {
      emit('page-change', newPage)
      emit('update:page', newPage)
    })

    return {
      searchQuery,
      currentPage,
      itemsPerPage,
      sortColumn,
      sortDirection,
      selectedRows,
      perPageId,
      isClickable,
      filteredData,
      sortedData,
      paginatedData,
      totalPages,
      isAllSelected,
      isSomeSelected,
      ariaSort,
      sortBy,
      toggleRow,
      toggleSelectAll,
      handleRowKeydown
    }
  }
}
</script>
